import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";

import {
  GIVE_UP_AFTER_MS,
  POLL_MS,
  useDashboardPolling,
} from "./company-dashboard-polling";
import type { EngagementTiles, Tile } from "./company-dashboard-tiles";

export const DASHBOARD_ENGAGEMENT_PATH = "/api/v1/dashboard/engagement/";

// `value` is what the endpoint takes (`2026-Q4`), `label` Platform's English
// name for it (`Q4 2026`).
export type Quarter = { value: string; label: string };

export type ShownQuarter = Quarter & {
  in_progress: boolean;
  // The UTC day every figure is cumulative to, `2026-10-23`: the quarter's
  // last day, or today while it is under way.
  data_through: string;
};

// One office, one department, or managers ("true") or everyone else
// ("false"), one at a time; null is everyone. Office and department names are the client's
// own, exactly as Platform lists them in `groups`.
export type Filters = {
  location: string | null;
  business_unit: string | null;
  manager: "true" | "false" | null;
};

export type FilterName = keyof Filters;

export const NO_FILTERS: Filters = {
  location: null,
  business_unit: null,
  manager: null,
};

// The groups that may be chosen for the quarter: only groups big enough to
// show. Empty when the client has shared no roster.
export type Groups = {
  location: string[];
  business_unit: string[];
  manager: ("true" | "false")[];
};

// Written comments left after sessions and on-demand content, latest first
// and verbatim, by anyone in the company up to `data_through` whatever the
// filters. Withheld until enough people have commented, with no count.
export type Comments =
  | { suppressed: true; value: null }
  | { suppressed: false; value: { text: string }[] };

// A department the `business_unit` filter offers, with the two counts its
// filter shows, as of `data_through`. Listed highest activation rate first;
// a department either count of would be withheld is left out.
export type Team = {
  name: string;
  employees_in_scope: number;
  activated: number;
};

// Activation for the whole company whatever the filters, as of
// `data_through`: the unfiltered page's two tiles, and the departments.
export type Teams = {
  company: { employees_in_scope: Tile<number>; activated: Tile<number> };
  departments: Team[];
};

export type EngagementReport = {
  company_name: string;
  reused: boolean;
  quarter: ShownQuarter;
  // Newest first, listed at the moment of answering.
  quarters: Quarter[];
  filters: Filters;
  groups: Groups;
  tiles: EngagementTiles;
  // Absent from a Platform older than the web bundle.
  comments?: Comments;
  // Whatever the filters; empty with no department to show. Absent from a
  // Platform older than the web bundle.
  teams?: Teams;
};

type EngagementResponse =
  | ({ status: "ready" } & EngagementReport)
  | {
      status: "running";
      handle: string;
      quarter: ShownQuarter;
      quarters: Quarter[];
      filters: Filters;
      groups?: Groups;
    };

export type EngagementState =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; report: EngagementReport };

type EngagementContextValue = {
  status: EngagementState;
  quarters: Quarter[];
  // The quarter shown or being asked for, as its `value`; null until Platform
  // has named the current one.
  selected: string | null;
  // The groups the figures are for, or are being asked for.
  filters: Filters;
  // What the filters may offer, from the last answer that knew.
  groups: Groups | null;
  // The quarter Platform last described for the selection, so its "data
  // through" day shows while its figures are still being worked out.
  shown: ShownQuarter | null;
  // From the last quarter that loaded, so it stays put while another loads.
  companyName: string | null;
  open: () => void;
  select: (quarter: string) => void;
  filter: (name: FilterName, value: string | null) => void;
  clearFilters: () => void;
  retry: () => void;
};

const EngagementContext = createContext<EngagementContextValue | null>(null);

const startPath = (quarter: string | null, filters: Filters) => {
  const params = new URLSearchParams();
  if (quarter !== null) params.set("quarter", quarter);
  (Object.keys(filters) as FilterName[]).forEach((name) => {
    const value = filters[name];
    if (value !== null) params.set(name, value);
  });
  const query = params.toString();
  return query === ""
    ? DASHBOARD_ENGAGEMENT_PATH
    : `${DASHBOARD_ENGAGEMENT_PATH}?${query}`;
};

// Every view is its own: the same quarter for another group is another entry,
// so one view is never shown another's figures.
const viewKey = (quarter: string, filters: Filters) =>
  JSON.stringify([
    quarter,
    filters.location,
    filters.business_unit,
    filters.manager,
  ]);

/**
 * The redesigned Engagement page's figures, one quarter and one set of group
 * filters at a time, each cumulative as of the quarter's end.
 *
 * Asked the way the Resilience page's are: nothing until the page opens
 * (`open`), the first ask names no quarter so Platform picks the one under
 * way, and a 202 is checked again with its newest handle until the figures
 * are ready. Each view's figures are kept while the dashboard is open.
 */
export function DashboardEngagementProvider({
  children,
  pollMs = POLL_MS,
  giveUpAfterMs = GIVE_UP_AFTER_MS,
}: {
  children: ReactNode;
  // Overridden only by tests, so polling and the give-up run without the wait.
  pollMs?: number;
  giveUpAfterMs?: number;
}) {
  const [status, setStatus] = useState<EngagementState>({ state: "idle" });
  const [quarters, setQuarters] = useState<Quarter[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [groups, setGroups] = useState<Groups | null>(null);
  const [shown, setShown] = useState<ShownQuarter | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const cache = useRef(new Map<string, EngagementReport>());
  const requested = useRef<string | null>(null);
  const requestedFilters = useRef<Filters>(NO_FILTERS);
  const opened = useRef(false);
  const { request, cancel } = useDashboardPolling({
    path: DASHBOARD_ENGAGEMENT_PATH,
    name: "engagement",
    pollMs,
    giveUpAfterMs,
  });

  const load = useCallback(
    (quarter: string | null, wanted: Filters) => {
      cancel();
      requested.current = quarter;
      requestedFilters.current = wanted;
      setSelected(quarter);
      setFilters(wanted);

      const cached =
        quarter === null
          ? undefined
          : cache.current.get(viewKey(quarter, wanted));
      if (cached) {
        setShown(cached.quarter);
        setGroups(cached.groups);
        setStatus({ state: "ready", report: cached });
        return;
      }

      setShown(null);
      setStatus({ state: "loading" });
      // Asked without a quarter, Platform names the one it started; every
      // later ask, a restart included, names that quarter.
      let asked = quarter;

      request<EngagementResponse>({
        startPath: () => startPath(asked, wanted),
        onAnswer: (response) => {
          if (typeof response.quarter?.value !== "string") {
            throw new Error("Engagement answered without naming its quarter");
          }
          setQuarters(response.quarters);
          setShown(response.quarter);
          if (response.groups) setGroups(response.groups);
          if (asked === null) {
            asked = response.quarter.value;
            requested.current = asked;
            setSelected(asked);
          }
          if (response.status === "ready") {
            cache.current.set(viewKey(asked, wanted), response);
            setCompanyName(response.company_name);
            setStatus({ state: "ready", report: response });
          }
        },
        onError: () => setStatus({ state: "error" }),
      });
    },
    [request, cancel]
  );

  // A remounted page (Fast Refresh, StrictMode) has to be able to ask again.
  useEffect(
    () => () => {
      opened.current = false;
    },
    []
  );

  const open = useCallback(() => {
    if (opened.current) return;
    opened.current = true;
    load(null, NO_FILTERS);
  }, [load]);

  const value = useMemo(
    () => ({
      status,
      quarters,
      selected,
      filters,
      groups,
      shown,
      companyName,
      open,
      select: (quarter: string) => load(quarter, requestedFilters.current),
      // One at a time: choosing a group resets the others to everyone.
      filter: (name: FilterName, value: string | null) =>
        load(requested.current, { ...NO_FILTERS, [name]: value }),
      clearFilters: () => load(requested.current, NO_FILTERS),
      retry: () => load(requested.current, requestedFilters.current),
    }),
    [
      status,
      quarters,
      selected,
      filters,
      groups,
      shown,
      companyName,
      open,
      load,
    ]
  );

  return (
    <EngagementContext.Provider value={value}>
      {children}
    </EngagementContext.Provider>
  );
}

export function useDashboardEngagement(): EngagementContextValue {
  const value = useContext(EngagementContext);
  if (value === null) {
    throw new Error(
      "useDashboardEngagement needs a DashboardEngagementProvider above it"
    );
  }
  return value;
}
